import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string | undefined>} The section HTML, or undefined for a superseded failure
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = 'full' } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    try {
      const sectionHTML = await this.getSectionHTML(sectionId, cache, url);
      if (!abortController.signal.aborted) await morphSection(sectionId, sectionHTML, mode);
      return sectionHTML;
    } catch (error) {
      // A newer render owns this section, including its failure handling.
      if (!abortController.signal.aborted) throw error;
    } finally {
      if (this.#abortControllersBySectionId.get(sectionId) === abortController) {
        this.#abortControllersBySectionId.delete(sectionId);
      }
    }
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    const categoryConfig = document.querySelector('script[data-z-colour-engine]');
    const config = categoryConfig ? JSON.parse(categoryConfig.textContent) : null;
    const isCategory = config && normalizeSectionId(sectionId) === config.sectionId &&
      url.pathname.endsWith('/search') && url.searchParams.get('view') === 'zentus-category';
    const isColourCategory = isCategory && url.searchParams.has('filter.v.option.color');
    const isResults = (isCategory || url.pathname.includes('/collections/')) &&
      document.getElementById(buildSectionSelector(normalizeSectionId(sectionId)))?.querySelector('results-list');
    pendingPromise = isColourCategory
      ? import(config.module)
        .then(engine => engine.getCategorySectionHTML(sectionId, new URL(url), config))
        .then(html => {
          if (!validResultsHTML(html, sectionId)) throw new Error('Incomplete colour results response');
          return html;
        })
      : isResults
        ? readResultsHTML(sectionUrl, sectionId)
        : fetch(sectionUrl).then(async response => {
          const html = await response.text();
          if (!response.ok || !getValidSection(html, sectionId)) throw new Error('Invalid section response');
          return html;
        });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    let sectionHTML;
    try {
      sectionHTML = await pendingPromise;
    } finally {
      this.#pendingPromises.delete(sectionUrl);
    }

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      // Category filter markup is hydrated from the native colour index.
      if (section.querySelector('script[data-z-colour-engine]')) continue;
      if (section.querySelector('results-list') && !validResultsSection(section)) continue;
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

function validResultsSection(section) {
  const list = section?.querySelector('results-list');
  const expected = list?.querySelector('[data-z-expected]')?.getAttribute('data-z-expected')?.trim();
  return Boolean(list && /^\d+$/.test(expected ?? '') &&
    (Number(expected) === 0 || list.querySelector('.product-grid__item, .zxe-empty-category')));
}

function getValidSection(html, sectionId) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  if (fragment.body.classList.contains('status-error')) return null;
  return fragment.getElementById(buildSectionSelector(normalizeSectionId(sectionId)));
}

export function validResultsHTML(html, sectionId) {
  if (/Liquid (?:error|syntax error)|There was a problem loading this website/i.test(html)) return false;
  return validResultsSection(getValidSection(html, sectionId));
}

async function readResultsHTML(url, sectionId) {
  for (let attempt = 0; attempt < 2; attempt++) {
    const request = new URL(url);
    if (attempt) request.searchParams.set('_z_colour_retry', '1');
    try {
      const response = await fetch(request);
      const html = await response.text();
      if (response.ok && validResultsHTML(html, sectionId)) return html;
    } catch {
      // A failed request gets the same single retry as incomplete rendered HTML.
    }
  }
  throw new Error('Results response failed after retry');
}

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {'hydration'|'full'} [mode] - Which parts of the section to morph into the DOM. 'hydration' will only morph nodes with `data-hydration-key` attributes.
 */
export async function morphSection(sectionId, html, mode = 'full') {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === 'hydration',
  });
}

export const sectionRenderer = new SectionRenderer();
